// Offline data pre-warm (2026-10-31, Jaime directive: "the app should preload
// all units' info in the background after loading the initial screen").
//
// DEFECT (v1.0.131 live, airplane-mode session 2026-10-30): switching offline
// to any unit other than the one active while online produced apiError 503
// OFFLINE envelopes for /home/tasks + /home/shopping, because the SW runtime
// cache only held responses for the context that happened to be active while
// connected.
//
// LAYERING (verified): public/sw.js intercepts EVERY page request (axios XHR
// included) before the network and serves same-origin /api GETs network-first
// with cache fallback. A cached response resolves the page fetch with HTTP
// 200, so api.ts's synthesized 503 (axios response interceptor, only when
// `!error.response && navigator.onLine === false`) NEVER fires for a
// prefetch-covered endpoint — the SW answers first. The 503 envelope remains
// the honest fallback for endpoints with no cached copy.
//
// This module queues a LOW-PRIORITY background prefetch, after boot + the
// initial screen render, of every per-context GET the offline flows need:
//   - per unit (the explicit unitId query param makes each URL cache-distinct
//     AND drives the X-Active-Unit-Id header via the axios interceptor):
//     /home/tasks, /home/shopping (+ sort-order, custom-lists), /home/budget,
//     /home/vehicles, /home/pets, /home/equipment (+ reminders) — the data
//     behind Resumen/Tareas/Compras/Presupuesto/Vehículos/Mascotas/Equipos
//   - per (condo, unit) + per condo + global: /entitlements/me — the menu's
//     plan gate (isPlanVisible) resolves per context
//   - per condo + global: /me/permissions — usePermissions' can()/hasFeature()
//     resolve per context
// All requests are GETs under /api/ (same-origin), so sw.js's network-first +
// cache-fallback branch stores each of them for offline reuse.
//
// Rules: skip entirely when offline; serialize with low concurrency + small
// delays; re-run on reconnect only when the last completed run is older than
// PRELOAD_STALE_MS or the context fingerprint changed. Failures are swallowed
// (a missed entry simply falls back to today's 503 envelope offline).

import api from '../services/api';
import { traceSel } from '../utils/selTrace';
import { buildPreloadJobs, fingerprintOf } from './offlinePreloadJobs';
import type { PreloadInput, PreloadJob } from './offlinePreloadJobs';

export type { PreloadUnit, PreloadInput, PreloadJob } from './offlinePreloadJobs';
export { buildPreloadJobs, fingerprintOf, UNIT_HOME_PATHS } from './offlinePreloadJobs';

// Re-run threshold: a completed prefetch stays "fresh" for 6h. A reconnect
// inside the window does not re-hit the API; past the window it does.
export const PRELOAD_STALE_MS = 6 * 60 * 60 * 1000;
// Minimum spacing between ATTEMPTS (even no-op ones) so a flapping connection
// or rapid effect re-runs can never turn into a request loop.
const MIN_ATTEMPT_GAP_MS = 60 * 1000;
// Initial-screen grace: let boot's own fetches (refreshMe, dashboard, menus)
// go first, then warm the cache during idle time.
const START_DELAY_MS = 2500;
const CONCURRENCY = 2;
const INTER_JOB_DELAY_MS = 200;

const PRELOAD_STATE_KEY = 'vecindus_offline_preload';

interface PreloadState { lastAttemptAt?: number; lastRunAt?: number; fingerprint?: string; ok?: number; failed?: number; reason?: string; }

function readState(): PreloadState {
  try {
    const raw = localStorage.getItem(PRELOAD_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PreloadState;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
}

function writeState(patch: PreloadState): void {
  try { localStorage.setItem(PRELOAD_STATE_KEY, JSON.stringify({ ...readState(), ...patch })); } catch { /* storage optional */ }
}

export function readOfflinePreloadState(): PreloadState {
  return readState();
}

export interface PreloadResult { ok: number; failed: number; total: number; skipped: boolean; }

let running: Promise<PreloadResult> | null = null;

async function runJobs(jobs: PreloadJob[]): Promise<PreloadResult> {
  let ok = 0; let failed = 0; let idx = 0;
  const worker = async () => {
    while (idx < jobs.length) {
      const job = jobs[idx++];
      try {
        await job.run();
        ok++;
      } catch (err) {
        failed++;
        // One entry per FAILURE only — the 50-entry sel-trace ring must keep
        // room for the offline api/apiError entries QA correlates on.
        traceSel('preloadFail', { key: job.key, status: (err as { response?: { status?: number } })?.response?.status ?? null });
      }
      await new Promise((r) => setTimeout(r, INTER_JOB_DELAY_MS));
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(CONCURRENCY, jobs.length)) }, worker));
  return { ok, failed, total: jobs.length, skipped: false };
}

/**
 * Schedule the background prefetch. Fire-and-forget; safe to call on every
 * boot/refreshMe/reconnect — throttling, staleness and fingerprint checks
 * dedupe internally. Never runs offline.
 */
export function scheduleOfflinePreload(input: PreloadInput): void {
  if (typeof window === 'undefined') return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    traceSel('preloadSkip', { reason: 'offline' });
    return;
  }
  try {
    if (!localStorage.getItem('token')) return;
  } catch { return; }
  const now = Date.now();
  const state = readState();
  const fingerprint = fingerprintOf(input);
  const lastAttempt = state.lastAttemptAt || 0;
  if (now - lastAttempt < MIN_ATTEMPT_GAP_MS) return;
  const fingerprintChanged = state.fingerprint !== fingerprint;
  const stale = !state.lastRunAt || now - state.lastRunAt > PRELOAD_STALE_MS;
  if (!fingerprintChanged && !stale && state.lastRunAt) return;
  // Nit-d fix (kimi re-gate review, 2026-10-31): stamp the attempt AFTER the
  // in-flight check. Writing lastAttemptAt/fingerprint before it let a merely
  // in-flight run suppress a fingerprint update (state showed the NEW
  // fingerprint while the running sweep used the OLD inputs, and the
  // attempt-gap throttle then blocked the corrected re-run).
  if (running) return; // a run is already in flight with current inputs
  writeState({ lastAttemptAt: now, fingerprint });
  const delay = state.lastRunAt ? 0 : START_DELAY_MS; // first boot waits for the initial screen
  const timer = setTimeout(() => {
    const jobs = buildPreloadJobs(input, (url, config) => api.get(url, config as never));
    if (!jobs.length) return;
    traceSel('preloadStart', { reason: input.reason, jobs: jobs.length, units: input.units.length, condos: input.condoIds.length });
    running = runJobs(jobs)
      .then((result) => {
        writeState({ lastRunAt: Date.now(), ok: result.ok, failed: result.failed, reason: input.reason });
        traceSel('preloadDone', { ok: result.ok, failed: result.failed, total: result.total, reason: input.reason });
        return result;
      })
      .catch(() => ({ ok: 0, failed: 0, total: jobs.length, skipped: true } as PreloadResult))
      .finally(() => { running = null; });
  }, delay);
  // A pending timer must never block page teardown (node-style hint; browser
  // timers simply ignore it).
  try { (timer as unknown as { unref?: () => void }).unref?.(); } catch { /* noop */ }
}
